import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { getAuthenticatedAxios } from '../../utils/api';
import { 
  Shuffle, 
  Play, 
  Loader2, 
  Users, 
  Coins,
  Clock,
  TrendingDown,
  Settings,
  RefreshCw,
  Square,
  ExternalLink,
  History
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const BlumRandomBuy = () => {
  const [walletGroups, setWalletGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [availableTokens, setAvailableTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const statusCheckRef = useRef(null);
  
  // Settings
  const [config, setConfig] = useState({
    amount_ton: 0.1,
    slippage_percent: 5.0,
    min_liquidity: 10.0,
    
    // Delay settings
    delay_min_sec: 1.0,
    delay_max_sec: 10.0,
    
    // Auto-sell settings
    auto_sell_enabled: false,
    auto_sell_percent: 95.0,
    auto_sell_random: false,
    auto_sell_min_percent: 90.0,
    auto_sell_max_percent: 100.0,
    sell_delay_min_sec: 5.0,
    sell_delay_max_sec: 30.0,
    
    // Exclude already bought
    exclude_bought_tokens: true
  });
  
  useEffect(() => {
    loadData();
    checkStatus();
    loadHistory();
    
    // Cleanup on unmount
    return () => {
      if (statusCheckRef.current) {
        clearInterval(statusCheckRef.current);
      }
    };
  }, []);
  
  const checkStatus = async () => {
    try {
      const api = getAuthenticatedAxios();
      const res = await api.get(`${API_URL}/api/blum/random-buy/status`);
      
      if (res.data.success) {
        setSessionStatus(res.data);
        
        // If running, start polling
        if (res.data.running && !statusCheckRef.current) {
          setExecuting(true);
          statusCheckRef.current = setInterval(checkStatus, 2000);
        } else if (!res.data.running && statusCheckRef.current) {
          clearInterval(statusCheckRef.current);
          statusCheckRef.current = null;
          setExecuting(false);
          loadHistory();
        }
      }
    } catch (error) {
      console.error('Error checking status:', error);
    }
  };
  
  const loadHistory = async () => {
    try {
      const api = getAuthenticatedAxios();
      const [sessionsRes, purchasesRes] = await Promise.all([
        api.get(`${API_URL}/api/blum/random-buy/sessions?limit=5`),
        api.get(`${API_URL}/api/blum/random-buy/purchases?limit=20`)
      ]);
      
      if (sessionsRes.data.success) {
        setRecentSessions(sessionsRes.data.sessions || []);
      }
      if (purchasesRes.data.success) {
        setPurchases(purchasesRes.data.purchases || []);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };
  
  const loadData = async () => {
    setLoading(true);
    try {
      const api = getAuthenticatedAxios();
      
      const [groupsRes, tokensRes] = await Promise.all([
        api.get(`${API_URL}/api/blum/wallet-groups`),
        api.get(`${API_URL}/api/blum/available-tokens?limit=200`)
      ]);
      
      if (groupsRes.data.success) {
        setWalletGroups(groupsRes.data.groups || []);
      }
      
      if (tokensRes.data.success) {
        setAvailableTokens(tokensRes.data.tokens || []);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };
  
  const handleExecute = async () => {
    if (!selectedGroup) {
      toast.error('Выберите группу кошельков');
      return;
    }
    
    if (config.amount_ton < 0.1) {
      toast.error('Минимальная сумма 0.1 TON');
      return;
    }
    
    setExecuting(true);
    setResult(null);
    
    // Start status polling
    statusCheckRef.current = setInterval(checkStatus, 2000);
    
    try {
      const api = getAuthenticatedAxios();
      
      const response = await api.post(`${API_URL}/api/blum/trade/random-buy`, {
        group_id: selectedGroup,
        amount_ton: config.amount_ton,
        slippage_percent: config.slippage_percent,
        min_liquidity: config.min_liquidity,
        delay_min_sec: config.delay_min_sec,
        delay_max_sec: config.delay_max_sec,
        auto_sell_enabled: config.auto_sell_enabled,
        auto_sell_percent: config.auto_sell_percent,
        auto_sell_random: config.auto_sell_random,
        auto_sell_min_percent: config.auto_sell_min_percent,
        auto_sell_max_percent: config.auto_sell_max_percent,
        sell_delay_min_sec: config.sell_delay_min_sec,
        sell_delay_max_sec: config.sell_delay_max_sec,
        exclude_bought_tokens: config.exclude_bought_tokens
      });
      
      setResult(response.data);
      
      if (response.data.success) {
        toast.success(`Выполнено: ${response.data.successful_buys}/${response.data.total_wallets} покупок`);
        if (response.data.successful_sells > 0) {
          toast.success(`Авто-продаж: ${response.data.successful_sells}`);
        }
      } else {
        toast.error(response.data.error || 'Ошибка выполнения');
      }
      
    } catch (error) {
      console.error('Error executing random buy:', error);
      toast.error(error.response?.data?.detail || 'Ошибка выполнения');
    } finally {
      setExecuting(false);
      if (statusCheckRef.current) {
        clearInterval(statusCheckRef.current);
        statusCheckRef.current = null;
      }
      loadHistory();
    }
  };
  
  const handleStop = async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/blum/random-buy/stop`);
      
      if (response.data.success) {
        toast.info('Остановка запрошена...');
      } else {
        toast.error(response.data.error || 'Ошибка остановки');
      }
    } catch (error) {
      console.error('Error stopping:', error);
      toast.error('Ошибка остановки');
    }
  };
  
  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shuffle className="w-6 h-6 text-purple-400" />
          <h2 className="text-xl font-bold text-white">Random Buy</h2>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={loadData}
          className="bg-white/5 border-white/10 text-purple-200"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Обновить
        </Button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Settings */}
        <div className="space-y-4">
          {/* Group Selection */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-purple-200 text-sm flex items-center gap-2">
                <Users className="w-4 h-4" />
                Группа кошельков
              </CardTitle>
            </CardHeader>
            <CardContent>
              <select
                value={selectedGroup || ''}
                onChange={(e) => setSelectedGroup(e.target.value || null)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white"
              >
                <option value="">Выберите группу</option>
                {walletGroups.map((group) => (
                  <option key={group.group_name} value={group.group_name}>
                    {group.group_name} ({group.count} кошельков)
                  </option>
                ))}
              </select>
            </CardContent>
          </Card>
          
          {/* Amount Settings */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-purple-200 text-sm flex items-center gap-2">
                <Coins className="w-4 h-4" />
                Параметры покупки
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-purple-200 text-xs block mb-1">Сумма TON (мин. 0.1)</label>
                <Input
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={config.amount_ton}
                  onChange={(e) => updateConfig('amount_ton', parseFloat(e.target.value) || 0.1)}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              
              <div>
                <label className="text-purple-200 text-xs block mb-1">Мин. ликвидность (TON)</label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={config.min_liquidity}
                  onChange={(e) => updateConfig('min_liquidity', parseFloat(e.target.value) || 0)}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={config.exclude_bought_tokens}
                  onChange={(e) => updateConfig('exclude_bought_tokens', e.target.checked)}
                  className="w-4 h-4 rounded"
                />
                <label className="text-purple-200 text-sm">
                  Не покупать токены которые уже покупались
                </label>
              </div>
            </CardContent>
          </Card>
          
          {/* Delay Settings */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-purple-200 text-sm flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Задержки между операциями
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-purple-200 text-xs block mb-1">Мин. (сек)</label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0.1"
                    value={config.delay_min_sec}
                    onChange={(e) => updateConfig('delay_min_sec', parseFloat(e.target.value) || 1)}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-purple-200 text-xs block mb-1">Макс. (сек)</label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0.1"
                    value={config.delay_max_sec}
                    onChange={(e) => updateConfig('delay_max_sec', parseFloat(e.target.value) || 10)}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
              <p className="text-gray-400 text-xs">
                Кошельки будут действовать в случайном порядке с рандомными задержками
              </p>
            </CardContent>
          </Card>
        </div>
        
        {/* Right Column - Auto-Sell & Execute */}
        <div className="space-y-4">
          {/* Auto-Sell Settings */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-purple-200 text-sm flex items-center gap-2">
                <TrendingDown className="w-4 h-4" />
                Авто-продажа после покупки
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={config.auto_sell_enabled}
                  onChange={(e) => updateConfig('auto_sell_enabled', e.target.checked)}
                  className="w-4 h-4 rounded"
                />
                <label className="text-purple-200 text-sm">
                  Включить авто-продажу
                </label>
              </div>
              
              {config.auto_sell_enabled && (
                <>
                  <div>
                    <label className="text-purple-200 text-xs block mb-1">
                      Процент продажи ({config.auto_sell_percent}%)
                    </label>
                    <input
                      type="range"
                      min="50"
                      max="100"
                      value={config.auto_sell_percent}
                      onChange={(e) => updateConfig('auto_sell_percent', parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={config.auto_sell_random}
                      onChange={(e) => updateConfig('auto_sell_random', e.target.checked)}
                      className="w-4 h-4 rounded"
                    />
                    <label className="text-purple-200 text-sm">
                      Рандом (для естественности)
                    </label>
                  </div>
                  
                  {config.auto_sell_random && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-purple-200 text-xs block mb-1">
                          Мин. % продажи
                        </label>
                        <Input
                          type="number"
                          step="1"
                          min="1"
                          max="100"
                          value={config.auto_sell_min_percent}
                          onChange={(e) => updateConfig('auto_sell_min_percent', parseFloat(e.target.value) || 90)}
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-purple-200 text-xs block mb-1">
                          Макс. % продажи
                        </label>
                        <Input
                          type="number"
                          step="1"
                          min="1"
                          max="100"
                          value={config.auto_sell_max_percent}
                          onChange={(e) => updateConfig('auto_sell_max_percent', parseFloat(e.target.value) || 100)}
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-purple-200 text-xs block mb-1">
                        Задержка мин. (сек)
                      </label>
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        value={config.sell_delay_min_sec}
                        onChange={(e) => updateConfig('sell_delay_min_sec', parseFloat(e.target.value) || 5)}
                        className="bg-white/5 border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-purple-200 text-xs block mb-1">
                        Задержка макс. (сек)
                      </label>
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        value={config.sell_delay_max_sec}
                        onChange={(e) => updateConfig('sell_delay_max_sec', parseFloat(e.target.value) || 30)}
                        className="bg-white/5 border-white/10 text-white"
                      />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          
          {/* Stats */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-purple-200 text-sm flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Статистика
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Доступно токенов:</span>
                  <span className="text-white ml-2">{availableTokens.length}</span>
                </div>
                <div>
                  <span className="text-gray-400">Групп кошельков:</span>
                  <span className="text-white ml-2">{walletGroups.length}</span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Execute / Stop Buttons */}
          <div className="flex gap-2">
            {executing ? (
              <Button
                onClick={handleStop}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-6"
              >
                <Square className="w-5 h-5 mr-2" />
                Остановить
              </Button>
            ) : (
              <Button
                onClick={handleExecute}
                disabled={!selectedGroup}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-6"
              >
                <Play className="w-5 h-5 mr-2" />
                Запустить Random Buy
              </Button>
            )}
            
            <Button
              onClick={() => setShowHistory(!showHistory)}
              variant="outline"
              className="bg-white/5 border-white/10 text-purple-200 py-6"
            >
              <History className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Running Status */}
          {executing && sessionStatus && (
            <Card className="bg-blue-900/20 border-blue-500/30">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                  <span className="text-blue-400 font-medium">
                    Сессия {sessionStatus.session_id} выполняется...
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Results */}
          {result && (
            <Card className={`${result.success ? 'bg-green-900/20 border-green-500/30' : 'bg-red-900/20 border-red-500/30'}`}>
              <CardContent className="pt-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Всего кошельков:</span>
                    <span className="text-white">{result.total_wallets}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Успешных покупок:</span>
                    <span className="text-green-400">{result.successful_buys}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Неудачных:</span>
                    <span className="text-red-400">{result.failed_buys}</span>
                  </div>
                  {result.successful_sells > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Авто-продаж:</span>
                      <span className="text-blue-400">{result.successful_sells}</span>
                    </div>
                  )}
                </div>
                
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <p className="text-red-400 text-xs">
                      Ошибки: {result.errors.slice(0, 3).map(e => e.error || e).join(', ')}
                    </p>
                  </div>
                )}
                
                {/* Transaction links */}
                {result.results && result.results.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/10 max-h-40 overflow-y-auto">
                    <p className="text-gray-400 text-xs mb-2">Транзакции:</p>
                    {result.results.filter(r => r.tx_hash).slice(0, 5).map((r, idx) => (
                      <a
                        key={idx}
                        href={`https://tonviewer.com/transaction/${r.tx_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                      >
                        <ExternalLink className="w-3 h-3" />
                        {r.wallet_address?.slice(0, 10)}... → {r.token?.symbol || 'token'}
                      </a>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* History Panel */}
          {showHistory && (
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-purple-200 text-sm flex items-center gap-2">
                  <History className="w-4 h-4" />
                  История покупок
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-80 overflow-y-auto">
                {purchases.length === 0 ? (
                  <p className="text-gray-400 text-sm">Нет покупок</p>
                ) : (
                  purchases.map((p, idx) => (
                    <div key={idx} className="bg-white/5 rounded-lg p-3 text-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="text-white">{p.wallet_address?.slice(0, 10)}...</span>
                          <span className="text-gray-400 ml-2">{p.amount_ton} TON</span>
                        </div>
                        {p.tonviewer_url && (
                          <a
                            href={p.tonviewer_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                      <div className="text-gray-400 text-xs mt-1">
                        {p.token_address?.slice(0, 15)}...
                        {p.sold_at && (
                          <span className="ml-2 text-green-400">
                            Продано
                            {p.sell_tonviewer_url && (
                              <a
                                href={p.sell_tonviewer_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-1 inline-flex"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </span>
                        )}
                      </div>
                      <div className="text-gray-500 text-xs mt-1">
                        {new Date(p.bought_at).toLocaleString('ru-RU')}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlumRandomBuy;
